// src/features/rooms/renderers/ColoredBadgeCellRenderer.tsx
import React from "react";
import type { ICellRendererParams } from "ag-grid-enterprise";

export type ColorMap = Record<string, string>;

export type ColoredBadgeRendererParams = {
  colorMap: ColorMap;

  /** optional: prefix for tooltip, e.g. "MMI" or "dR-status" */
  titlePrefix?: string;

  /** label when value is null/empty */
  noDataLabel?: string;

  /** background when no color match found */
  noColorBg?: string;

  /** optional: map raw value to key used in colorMap (default: String(trim)) */
  toKey?: (value: unknown) => string;

  /** optional: map raw value to what is shown (default: "–" / String(value)) */
  toLabel?: (value: unknown) => string;
};

export const ColoredBadgeCellRenderer: React.FC<
  ICellRendererParams<unknown, unknown> & ColoredBadgeRendererParams
> = (params) => {
  const {
    colorMap,
    titlePrefix,
    noDataLabel = "–",
    noColorBg = "var(--mantine-color-gray-1)",
    toKey,
    toLabel,
  } = params;

  const raw = params.value;

  const label =
    typeof toLabel === "function"
      ? toLabel(raw)
      : raw == null || raw === ""
        ? noDataLabel
        : typeof raw === "string"
          ? raw
          : typeof raw === "number"
            ? String(raw)
            : String(raw);

  const key =
    typeof toKey === "function"
      ? toKey(raw)
      : typeof raw === "string"
        ? raw.trim()
        : raw == null
          ? ""
          : String(raw).trim();

  const color = colorMap[key];

  const isColored = typeof color === "string" && color.length > 0;

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 6,
        fontVariantNumeric: "tabular-nums",
        background: isColored ? color : noColorBg,
        border: isColored ? "1px solid rgba(0,0,0,0.25)" : "1px solid var(--mantine-color-gray-4)",
        color: isColored ? "#111" : "var(--mantine-color-dark-6)",
        fontWeight: isColored ? 700 : 500,
        paddingInline: 6,
      }}
      title={titlePrefix ? `${titlePrefix} ${label}` : label}
    >
      {label}
    </div>
  );
};
